<template>
  <div class="refund-page">
    <header class="page-header">
      <button
        class="back-button"
        type="button"
        aria-label="返回"
        @click="goBack"
      >
        ‹
      </button>
      <h1>退款詳情</h1>
    </header>
    <main class="page-content">
      <section
        v-if="loading"
        class="card refund-card refund-skeleton"
        aria-label="退款資料載入中"
      >
        <div class="skeleton-line skeleton-status"></div>
        <div class="skeleton-line skeleton-amount"></div>
        <div class="divider"></div>
        <div class="skeleton-line skeleton-product"></div>
        <div class="skeleton-line skeleton-product short"></div>
        <div class="divider"></div>
        <div
          class="skeleton-line skeleton-meta"
          v-for="index in 3"
          :key="index"
        ></div>
        <div class="skeleton-line skeleton-note"></div>
      </section>
      <section
        v-else
        v-for="item in refundList"
        :key="item.id || item.refundNumber"
        class="card refund-card"
      >
        <div class="refund-status">
          {{ statusStr[item.status] || item.status }}
        </div>
        <div class="refund-amount">
          <span>退款金額：</span><strong>NT$ {{ item.price }}</strong>
        </div>
        <div class="divider"></div>
        <order-detail-spu-list-item
          v-for="refundItem in item.orderRefundItemList || []"
          :key="refundItem.id"
          :item-data="refundItem"
          :detail="detail"
        />
        <div class="divider"></div>
        <dl class="refund-meta">
          <div>
            <dt>退款原因</dt>
            <dd>{{ item.reason || "" }}</dd>
          </div>
          <div>
            <dt>售後單號</dt>
            <dd>{{ item.refundNumber || "" }}</dd>
          </div>
          <div>
            <dt>退款時間</dt>
            <dd>{{ formatDate(item.createTime) }}</dd>
          </div>
        </dl>
        <p class="refund-note">
          備註：退款金額根據用戶實際收到之商品和數量，重新計算金額。
        </p>
      </section>
      <h2>訂單內容</h2>
      <section class="card goods-card">
        <order-package-widget v-if="!loading && hasPackage" :detail="detail" />
        <refund-spu-list-widget v-else-if="!loading" :detail="detail" />
        <order-price-list :detail="detail" />
      </section>
      <section class="formula-note">
        <span>退款公式為</span><span>A=原支付金額</span
        ><span>B=依實際收到之商品和數量，重新計算最優惠活動</span
        ><span>退款金額=A-B-運費-購物袋</span>
      </section>
    </main>
  </div>
</template>

<script>
import { Toast } from "vant";
import { OrderApi } from "@/api";
import OrderDetailSpuListItem from "@/components/order/order-detail-spu-list-item";
import RefundSpuListWidget from "@/components/order/refund-spu-list-widget";
import OrderPackageWidget from "@/components/order/order-package-widget";
import OrderPriceList from "@/components/order/order-price-list";

const promotionGroups = (source) =>
  source
    ? (source.promotionItemList || [])
        .map((group) => ({
          promotion: group.promotion,
          promotionPrice: group.promotionPrice,
          orderItemList: group.orderItemList || [],
        }))
        .concat(
          (source.otherItemList || []).map((item) => ({
            promotion: null,
            promotionPrice: null,
            orderItemList: [item],
          })),
        )
    : [];

const getOrderItems = (detail) => {
  const items = (detail && detail.orderItemList) || [];
  if (items.length) return items;
  return promotionGroups(
    detail && (detail.promotionOrderItem || detail.refundPromotionOrderItem),
  ).reduce((all, group) => all.concat(group.orderItemList), []);
};

export default {
  name: "order-refund-page",
  components: {
    OrderDetailSpuListItem,
    RefundSpuListWidget,
    OrderPackageWidget,
    OrderPriceList,
  },
  data() {
    return {
      loading: true,
      detail: {},
      refundList: [],
      statusStr: {
        cancel: "用戶撤銷",
        unauthorize: "待審核",
        reject: "審核拒絕",
        unreturn: "待用戶寄回商品",
        uninstock: "待商家收件回庫",
        unreplace: "待商家換貨",
        unreceive: "待用戶收貨",
        waitRefund: "待商家退款",
        finishReplace: "已完成換貨",
        finishRefund: "已完成退款",
        fail: "退款失敗",
      },
    };
  },
  computed: {
    hasPackage() {
      return getOrderItems(this.detail).some((item) => Number(item.type) === 1);
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      const id = this.$route.query.id;
      if (!id) {
        Toast("缺少訂單編號");
        this.loading = false;
        return;
      }
      try {
        const [refundResponse, detailResponse] = await Promise.all([
          OrderApi.refundList(id),
          OrderApi.detail(id),
        ]);
        if (refundResponse.code !== 0)
          throw new Error(refundResponse.message || "取得退款資料失敗");
        if (detailResponse.code !== 0)
          throw new Error(detailResponse.message || "取得訂單資料失敗");
        this.refundList = refundResponse.data || [];
        this.detail = detailResponse.data || {};
      } catch (error) {
        Toast(error.message || "取得退款資料失敗");
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      return value ? this.$util.formatDate(value, "yyyy-MM-dd hh:mm") : "";
    },
    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style lang="less" scoped>
.refund-page {
  position: relative;
  height: 100vh;
  background: #f5f5f5;
  color: #323232;
  overflow: hidden;
}
.page-header {
  height: 52px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  position: fixed;
  top: 0;
  z-index: 1;
}
.page-header h1 {
  font-size: 17px;
  font-weight: 600;
}
.back-button {
  position: absolute;
  left: 12px;
  border: 0;
  background: transparent;
  color: #323232;
  font-size: 34px;
  line-height: 1;
  padding: 3px 8px;
}
.page-content {
  position: absolute;
  top: 52px;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}
.card {
  background: #fff;
  border-radius: 10px;
  padding: 14px 15px;
  margin-bottom: 14px;
}
.refund-status {
  font-size: 19px;
  font-weight: 700;
}
.refund-amount {
  margin-top: 10px;
  font-size: 13px;
  color: #909090;
}
.refund-amount strong {
  color: #323232;
}
.divider {
  border-top: 1px solid #f3f3f3;
  margin: 10px 0;
}
.refund-meta {
  margin: 0;
}
.refund-meta div {
  display: flex;
  justify-content: space-between;
  min-height: 34px;
  align-items: center;
  font-size: 14px;
}
.refund-meta dt {
  color: #323232;
}
.refund-meta dd {
  margin: 0;
  font-weight: 600;
  text-align: right;
}
.refund-note {
  color: #909090;
  font-size: 12px;
  border-top: 1px solid #f3f3f3;
  margin: 8px 0 0;
  padding-top: 10px;
  line-height: 1.5;
}
.refund-skeleton .skeleton-line {
  background: linear-gradient(90deg, #f1f1f1 25%, #f8f8f8 37%, #f1f1f1 63%);
  background-size: 400% 100%;
  border-radius: 4px;
  animation: skeleton-loading 1.4s ease infinite;
}
.skeleton-status {
  width: 30%;
  height: 22px;
}
.skeleton-amount {
  width: 45%;
  height: 14px;
  margin-top: 12px;
}
.skeleton-product {
  height: 46px;
  margin-bottom: 10px;
}
.skeleton-product.short {
  width: 72%;
}
.skeleton-meta {
  width: 65%;
  height: 14px;
  margin: 14px 0;
}
.skeleton-note {
  width: 88%;
  height: 12px;
  margin-top: 16px;
}
@keyframes skeleton-loading {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
h2 {
  font-size: 16px;
  padding: 0 5px 10px;
}
.goods-card {
  min-height: 150px;
  padding: 5px 15px 0;
}
.formula-note {
  color: #999;
  background: transparent;
  font-size: 14px;
  line-height: 1.5;
  padding: 16px 20px;
}
.formula-note span {
  display: block;
  white-space: nowrap;
}
</style>
